/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { FileText, RefreshCw, Search, Download, ChevronDown, AlertCircle, AlertTriangle, Info, Bug } from 'lucide-react';

type LogType = 'main' | 'skillshop' | 'velocity' | 'monitoring';

interface LogFile {
  filename: string;
  type: LogType;
  timestamp: string;
  isoTimestamp: string;
  size: number;
}

interface LogEntry {
  line: number;
  content: string;
  timestamp?: string;
  level?: 'debug' | 'info' | 'warn' | 'error';
  category?: string;
}

interface LogFilesResponse {
  success: boolean;
  data?: {
    files: LogFile[];
    logsDirectory: string;
    availableTypes: LogType[];
    message?: string;
  };
  error?: string;
}

interface LogContentResponse {
  success: boolean;
  data?: {
    filename: string;
    type: LogType;
    timestamp: string;
    size: number;
    totalLines: number;
    matchedLines: number;
    returnedLines: number;
    entries: LogEntry[];
    search: string | null;
  };
  error?: string;
}

const LOG_TYPE_LABELS: Record<LogType, string> = {
  main: 'Main Process',
  skillshop: 'SkillShop Server',
  velocity: 'Velocity Server',
  monitoring: 'Health Monitoring',
};

const LOG_TYPE_DESCRIPTIONS: Record<LogType, string> = {
  main: 'Electron main process startup and coordination',
  skillshop: 'SkillShop platform server (AI, auth, database)',
  velocity: 'Velocity backend server (API proxy, file operations)',
  monitoring: 'System health monitoring (JSON format)',
};

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatTimestamp(isoTimestamp: string): string {
  try {
    const date = new Date(isoTimestamp);
    return date.toLocaleString();
  } catch {
    return isoTimestamp;
  }
}

function LogLevelIcon({ level }: { level?: string }) {
  switch (level) {
    case 'error':
      return <AlertCircle size={14} style={{ color: '#dc2626', flexShrink: 0 }} />;
    case 'warn':
      return <AlertTriangle size={14} style={{ color: '#d97706', flexShrink: 0 }} />;
    case 'debug':
      return <Bug size={14} style={{ color: '#6b7280', flexShrink: 0 }} />;
    default:
      return <Info size={14} style={{ color: '#3b82f6', flexShrink: 0, opacity: 0.5 }} />;
  }
}

function getLogLineStyle(level?: string): React.CSSProperties {
  const base: React.CSSProperties = {
    fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Monaco, Consolas, monospace',
    fontSize: '12px',
    lineHeight: '1.5',
    padding: '2px 8px',
    borderLeft: '3px solid transparent',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
  };
  
  switch (level) {
    case 'error':
      return { ...base, backgroundColor: '#fef2f2', borderLeftColor: '#dc2626', color: '#991b1b' };
    case 'warn':
      return { ...base, backgroundColor: '#fffbeb', borderLeftColor: '#d97706', color: '#92400e' };
    case 'debug':
      return { ...base, backgroundColor: '#f9fafb', borderLeftColor: '#9ca3af', color: '#6b7280' };
    default:
      return { ...base, color: '#374151' };
  }
}

export function LogsSettings() {
  const [selectedType, setSelectedType] = useState<LogType>('main');
  const [logFiles, setLogFiles] = useState<LogFile[]>([]);
  const [selectedFile, setSelectedFile] = useState<LogFile | null>(null);
  const [logEntries, setLogEntries] = useState<LogEntry[]>([]);
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const [isLoadingContent, setIsLoadingContent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [showFileDropdown, setShowFileDropdown] = useState(false);
  const [logsDirectory, setLogsDirectory] = useState<string | null>(null);
  const [logStats, setLogStats] = useState<{ totalLines: number; matchedLines: number; returnedLines: number } | null>(null);
  
  const logContainerRef = useRef<HTMLDivElement>(null);
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);
  
  // Fetch log files list
  const fetchLogFiles = useCallback(async (type: LogType) => {
    setIsLoadingFiles(true);
    setError(null);
    
    try {
      const response = await fetch(`/api/logs/files?type=${type}&limit=20`);
      const data: LogFilesResponse = await response.json();
      
      if (data.success && data.data) {
        setLogFiles(data.data.files);
        setLogsDirectory(data.data.logsDirectory);
        
        // Auto-select the most recent file
        if (data.data.files.length > 0) {
          setSelectedFile(data.data.files[0]);
        } else {
          setSelectedFile(null);
          setLogEntries([]);
        }
      } else {
        setError(data.error || 'Failed to fetch log files');
        setLogFiles([]);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch log files');
      setLogFiles([]);
    } finally {
      setIsLoadingFiles(false);
    }
  }, []);
  
  // Fetch log content
  const fetchLogContent = useCallback(async (file: LogFile, search?: string) => {
    setIsLoadingContent(true);
    
    try {
      const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
      const response = await fetch(`/api/logs/content/${file.filename}?tail=1000${searchParam}`);
      const data: LogContentResponse = await response.json();
      
      if (data.success && data.data) {
        setLogEntries(data.data.entries);
        setLogStats({
          totalLines: data.data.totalLines,
          matchedLines: data.data.matchedLines,
          returnedLines: data.data.returnedLines,
        });
        
        // Scroll to bottom on initial load
        setTimeout(() => {
          if (logContainerRef.current) {
            logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
          }
        }, 100);
      } else {
        setError(data.error || 'Failed to fetch log content');
        setLogEntries([]);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch log content');
      setLogEntries([]);
    } finally {
      setIsLoadingContent(false);
    }
  }, []);
  
  // Load files when type changes
  useEffect(() => {
    fetchLogFiles(selectedType);
  }, [selectedType, fetchLogFiles]);
  
  // Load content when file or search changes
  useEffect(() => {
    if (selectedFile) {
      fetchLogContent(selectedFile, debouncedSearch);
    }
  }, [selectedFile, debouncedSearch, fetchLogContent]);
  
  // Auto-refresh functionality
  useEffect(() => {
    if (autoRefresh && selectedFile) {
      refreshIntervalRef.current = setInterval(() => {
        fetchLogContent(selectedFile, debouncedSearch);
      }, 3000);
    }
    
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [autoRefresh, selectedFile, debouncedSearch, fetchLogContent]);
  
  const handleRefresh = () => {
    if (selectedFile) {
      fetchLogContent(selectedFile, debouncedSearch);
    } else {
      fetchLogFiles(selectedType);
    }
  };
  
  const handleDownload = () => {
    if (!selectedFile || logEntries.length === 0) return;
    
    const content = logEntries.map(e => e.content).join('\n');
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = selectedFile.filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  
  return (
    <div style={{ 
      padding: '20px',
      background: '#f9f9f9',
      borderRadius: '8px',
      border: '1px solid #ddd'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
        <FileText size={20} style={{ marginRight: '8px', color: '#0066cc' }} />
        <h3 style={{ margin: 0 }}>Velocity Logs</h3>
      </div>
      
      <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '20px' }}>
        View application logs from the Velocity Electron app. Logs are stored in{' '}
        <code style={{ 
          background: '#e5e7eb', 
          padding: '2px 6px', 
          borderRadius: '4px',
          fontSize: '12px'
        }}>
          {logsDirectory || '~/.velocity/logs'}
        </code>
      </p>
      
      {/* Log Type Selector */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ 
          display: 'flex', 
          gap: '8px',
          flexWrap: 'wrap'
        }}>
          {(['main', 'skillshop', 'velocity', 'monitoring'] as LogType[]).map((type) => (
            <button
              key={type}
              onClick={() => setSelectedType(type)}
              title={LOG_TYPE_DESCRIPTIONS[type]}
              style={{
                padding: '8px 16px',
                fontSize: '13px',
                fontWeight: 500,
                border: selectedType === type ? '2px solid #0066cc' : '1px solid #d1d5db',
                borderRadius: '6px',
                background: selectedType === type ? '#eff6ff' : '#fff',
                color: selectedType === type ? '#0066cc' : '#374151',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
            >
              {LOG_TYPE_LABELS[type]}
            </button>
          ))}
        </div>
      </div>
      
      {/* Controls Row */}
      <div style={{ 
        display: 'flex', 
        gap: '12px', 
        marginBottom: '16px',
        alignItems: 'center',
        flexWrap: 'wrap'
      }}>
        {/* File Selector */}
        <div style={{ position: 'relative', minWidth: '280px' }}>
          <button
            onClick={() => setShowFileDropdown(!showFileDropdown)}
            disabled={isLoadingFiles}
            style={{
              width: '100%',
              padding: '8px 12px',
              fontSize: '13px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              background: '#fff',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '8px',
            }}
          >
            <span style={{ 
              overflow: 'hidden', 
              textOverflow: 'ellipsis', 
              whiteSpace: 'nowrap' 
            }}>
              {selectedFile 
                ? `${formatTimestamp(selectedFile.isoTimestamp)} (${formatFileSize(selectedFile.size)})`
                : 'No log files available'
              }
            </span>
            <ChevronDown size={16} style={{ flexShrink: 0 }} />
          </button>
          
          {showFileDropdown && logFiles.length > 0 && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              marginTop: '4px',
              background: '#fff',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              zIndex: 100,
              maxHeight: '300px',
              overflowY: 'auto',
            }}>
              {logFiles.map((file) => (
                <button
                  key={file.filename}
                  onClick={() => {
                    setSelectedFile(file);
                    setShowFileDropdown(false);
                  }}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    fontSize: '13px',
                    border: 'none',
                    borderBottom: '1px solid #f3f4f6',
                    background: selectedFile?.filename === file.filename ? '#eff6ff' : '#fff',
                    cursor: 'pointer',
                    textAlign: 'left',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <span>{formatTimestamp(file.isoTimestamp)}</span>
                  <span style={{ color: '#6b7280', fontSize: '12px' }}>
                    {formatFileSize(file.size)}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
        
        {/* Search Input */}
        <div style={{ position: 'relative', flex: 1, minWidth: '200px' }}>
          <Search size={16} style={{ 
            position: 'absolute', 
            left: '10px', 
            top: '50%', 
            transform: 'translateY(-50%)',
            color: '#9ca3af'
          }} />
          <input
            type="text"
            placeholder="Search logs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px 8px 36px',
              fontSize: '13px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              outline: 'none',
            }}
          />
        </div>
        
        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '8px' }}>
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 12px',
            fontSize: '13px',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            background: autoRefresh ? '#dcfce7' : '#fff',
            cursor: 'pointer',
          }}>
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            Auto-refresh
          </label>
          
          <button
            onClick={handleRefresh}
            disabled={isLoadingContent}
            title="Refresh logs"
            style={{
              padding: '8px 12px',
              fontSize: '13px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              background: '#fff',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <RefreshCw size={14} className={isLoadingContent ? 'spin' : ''} />
            Refresh
          </button>
          
          <button
            onClick={handleDownload}
            disabled={!selectedFile || logEntries.length === 0}
            title="Download log file"
            style={{
              padding: '8px 12px',
              fontSize: '13px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              background: '#fff',
              cursor: selectedFile ? 'pointer' : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              opacity: selectedFile ? 1 : 0.5,
            }}
          >
            <Download size={14} />
            Download
          </button>
        </div>
      </div>
      
      {/* Stats Row */}
      {logStats && (
        <div style={{
          display: 'flex',
          gap: '16px',
          marginBottom: '12px',
          fontSize: '12px',
          color: '#6b7280',
        }}>
          <span>Total lines: {logStats.totalLines.toLocaleString()}</span>
          {debouncedSearch && (
            <span>Matching: {logStats.matchedLines.toLocaleString()}</span>
          )}
          <span>Showing: {logStats.returnedLines.toLocaleString()}</span>
        </div>
      )}
      
      {/* Error Display */}
      {error && (
        <div style={{
          padding: '12px 16px',
          marginBottom: '16px',
          background: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '6px',
          color: '#991b1b',
          fontSize: '13px',
        }}>
          <AlertCircle size={16} style={{ display: 'inline', marginRight: '8px', verticalAlign: 'middle' }} />
          {error}
        </div>
      )}
      
      {/* Log Content */}
      <div
        ref={logContainerRef}
        style={{
          height: '500px',
          overflowY: 'auto',
          background: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: '6px',
        }}
      >
        {isLoadingFiles || (isLoadingContent && logEntries.length === 0) ? (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            color: '#6b7280',
          }}>
            <RefreshCw size={20} className="spin" style={{ marginRight: '8px' }} />
            Loading logs...
          </div>
        ) : logEntries.length === 0 ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            color: '#6b7280',
            gap: '8px',
          }}>
            <FileText size={32} style={{ opacity: 0.5 }} />
            <span>
              {logFiles.length === 0 
                ? 'No log files available. Logs are created when running the Electron app.'
                : debouncedSearch 
                  ? 'No matching log entries found.'
                  : 'Log file is empty.'
              }
            </span>
          </div>
        ) : (
          <div style={{ padding: '8px 0' }}>
            {logEntries.map((entry) => (
              <div
                key={entry.line}
                style={getLogLineStyle(entry.level)}
              >
                <LogLevelIcon level={entry.level} />
                <span style={{ 
                  color: '#9ca3af', 
                  fontSize: '11px',
                  width: '50px',
                  flexShrink: 0,
                  textAlign: 'right',
                }}>
                  {entry.line}
                </span>
                <span style={{ 
                  whiteSpace: 'pre-wrap', 
                  wordBreak: 'break-all',
                  flex: 1,
                }}>
                  {highlightSearch(entry.content, debouncedSearch)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Legend */}
      <div style={{
        display: 'flex',
        gap: '16px',
        marginTop: '12px',
        fontSize: '11px',
        color: '#6b7280',
      }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span style={{ width: '12px', height: '12px', background: '#fef2f2', border: '1px solid #dc2626', borderRadius: '2px' }} />
          Error
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span style={{ width: '12px', height: '12px', background: '#fffbeb', border: '1px solid #d97706', borderRadius: '2px' }} />
          Warning
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span style={{ width: '12px', height: '12px', background: '#f9fafb', border: '1px solid #9ca3af', borderRadius: '2px' }} />
          Debug
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span style={{ width: '12px', height: '12px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '2px' }} />
          Info
        </span>
      </div>
    </div>
  );
}

function highlightSearch(text: string, search: string): React.ReactNode {
  if (!search || !search.trim()) return text;
  
  const parts = text.split(new RegExp(`(${escapeRegExp(search)})`, 'gi'));
  
  return parts.map((part, i) => 
    part.toLowerCase() === search.toLowerCase() ? (
      <mark key={i} style={{ 
        background: '#fef08a', 
        padding: '0 2px',
        borderRadius: '2px',
      }}>
        {part}
      </mark>
    ) : (
      part
    )
  );
}

function escapeRegExp(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
